import { useState, useEffect } from "react";
import { useSession } from "next-auth/client";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Flex,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Checkbox,
  VStack,
  Input,
  useDisclosure,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import { Step, Steps, useSteps } from "chakra-ui-steps";
import { getAccount, updateUser } from "utils/sessionHelper";
import { firebaseClient } from "firebaseClient";
import { AiOutlineSelect } from "react-icons/ai";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAddSuccess?: () => void;
  agent?: any;
}

const MAX_FOLLOWERS = 105000;

export function CreateAgent({
  isOpen,
  onClose,
  onAddSuccess = () => { },
  agent = undefined,
}: Props) {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [agentName, setAgentName] = useState<string>("");
  const [prompt, setPrompt] = useState<string>("Analyze my Twitter account and take actions to generate engagement and grow my Twitter account following");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { nextStep, prevStep, activeStep, setStep } = useSteps({
    initialStep: 0,
  });
  const [session] = useSession();

  const stepOneUI = () => {
    return (
      <Box mt="0" mb={{ base: "10", md: "0" }} textAlign="left">
        <Text color="gray.500">
          Agents consist primarirely of a on a goal. Then the AI agent will use series of prompt and will connect to relevant sources to accomplish this goal.
          Nothing will be posted without your explicit consent.
          The AI agents will suggest actions that you can always choose to execute or ignore.
        </Text>

        <Box mt="5">
          <Text fontWeight="semibold" fontSize="lg">
            Name
          </Text>
          <Input
            size="md"
            placeholder="Repliers"
            borderRadius="lg"
            w="300px"
            mt="2"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
          />
        </Box>
        <Box mt="5">
          <Text fontWeight="semibold" fontSize="lg">
            Agent Goal
          </Text>
          <Input
            size="md"
            placeholder="SaaS Makers"
            borderRadius="lg"
            w="100%"
            mt="2"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </Box>
      </Box>
    )
  }

  const handleNext = async () => {

    let missingItems: string[] = [];

    if (!agentName) {
      missingItems.push("Watcher name");
    }

    if (missingItems?.length > 0) {
      const missingItemsLength = missingItems?.length;
      let missingItemString = "";
      if (missingItems?.length === 1) {
        missingItemString = missingItems[0];
      } else {
        const lastItem = missingItems.pop();
        missingItemString = missingItems.join(", ") + ` and ${lastItem}`;
      }
      toast.error(
        `${missingItemString} ${missingItemsLength === 1 ? "is" : "are"
        } required`
      );
      return;
    }

    setIsLoading(true);
    let newAgent: any = {
      name: agentName,
      prompt,
    };

    const db = firebaseClient.firestore();
    const query = db
      .collection("users")
      .doc(getAccount(session)?.id)
      .collection("accounts")
      .doc(getAccount(session)?.idAccount)
      .collection("agents");

    if (isEditMode) {
      await query.doc(agent.id).update(newAgent);
    } else {
      newAgent = {
        ...newAgent,
        status: "running",
        idUser: getAccount(session).id,
        idAccount: getAccount(session).idAccount,
        dateCreated: new Date(),
      };
      await query.add(newAgent);
    }

    toast.success("Watcher added!");
    setIsLoading(false);
    clearInputs();
    onAddSuccess();
  };

  const clearInputs = () => {
    setAgentName("");
  };

  const handleModalClose = () => {
    clearInputs();
    onClose();
  };

  useEffect(() => {
    if (session && isOpen && agent) {
      setIsEditMode(true);
      setAgentName(agent.name);
    }
  }, [isOpen, session]);

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={handleModalClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader fontWeight="semibold">
          {isEditMode ? `Editing ${agent.name}` : "Create a new agent"}
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" width="100%">
          {stepOneUI()}
          <Flex width="100%" mt="6" mb="2" justify="flex-end">
            <Button
              variant={"primary"}
              onClick={handleNext}
              isLoading={isLoading}
            >
              {isEditMode ? "Update Agent" : "Create Agent"}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
